import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { TextInput } from '@/components/forms/TextInput';
import type { BillingHistoryItem, BillingPlan, QuoteResponse } from '@pomoshnik/shared';
import type { AuthSubscription } from '@/lib/stores/authStore';
import { getSubscriptionPresentation } from './cabinetSubscription';
import { formatMoney } from './billingPresentation';

/**
 * Presenter Экрана 2.11.3 «Подписка / оплата» — Block 11 Phase 11.C (ADR-0093, ADR-0005).
 *
 * Чистая функция props → JSX. State + side effects (fetch plans/quote/history,
 * mount-fetch подписки) — в Container `SubscriptionScreen.tsx`.
 *
 * **Layout (P-046 shell):** `<main bg-canvas><Header/><section overflow-y-auto>
 * …</section><BottomBar variant="default"/></main>`. **VideoButton НЕ дублируем** —
 * это дочерний экран ЛК (P-040 только на entry; хаб 2.11.1 видео уже имеет).
 *
 * **ДЕНЬГИ (P-006):** presenter показывает суммы, которые посчитал бэк
 * (`plan.basePrice`, `quote.finalAmount`). Никаких клиентских расчётов скидок.
 * Кнопка «Оплатить/Продлить» = **заглушка** (реальный checkout — позже).
 *
 * **5 состояний (P-021):** plans (idle/loading/success/empty/error), quote
 * (idle/loading/success/error), history (idle/loading/success/empty/error).
 *
 * **Design-system:** карточки/инпут `bg-card` (P-038); body/headings `text-ink`
 * (P-039); кнопки вертикально (P-019); токены палитры (P-045); бейдж `#0D7A2E`
 * exception (P-022); размеры (P-044).
 */

export type AsyncState = 'idle' | 'loading' | 'success' | 'empty' | 'error';
export type QuoteState = 'idle' | 'loading' | 'success' | 'error';

const PAYMENT_STUB_TEXT = 'Переход к оплате — в разработке, доступно в следующих обновлениях';

const HISTORY_STATUS_LABEL: Record<BillingHistoryItem['status'], string> = {
  paid: 'Оплачено',
  pending: 'В обработке',
  failed: 'Ошибка',
  refunded: 'Возврат',
};

export interface SubscriptionScreenViewProps {
  // Статус подписки (server source of truth, /api/me)
  subscription: AuthSubscription | null;
  subscriptionLoading: boolean;
  daysRemaining: number | null;
  // Тариф/цена (GET plans)
  planState: AsyncState;
  plan: BillingPlan | null;
  // Промокод (POST quote)
  promoCode: string;
  onPromoCodeChange: (v: string) => void;
  quoteState: QuoteState;
  quote: QuoteResponse | null;
  quoteError?: string;
  onApplyPromo: () => void;
  // Оплата — заглушка
  onPay: () => void;
  paymentStubOpen: boolean;
  // История платежей (GET history)
  historyState: AsyncState;
  history: ReadonlyArray<BillingHistoryItem>;
}

export function SubscriptionScreenView(props: SubscriptionScreenViewProps) {
  const sub = props.subscription
    ? getSubscriptionPresentation(props.subscription.status, props.subscription.expiresAt)
    : null;

  const promoApplied = props.quoteState === 'success' && props.quote?.promoApplied === true;
  const applying = props.quoteState === 'loading';
  // Лейбл CTA = actionLabel из getSubscriptionPresentation (или fallback). Цену не
  // дублируем в кнопке — она показана в карточке тарифа выше (label-in-name, P-022).
  const payLabel = sub ? sub.actionLabel : 'Оплатить подписку';

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Подписка" />

      <section className="flex-1 p-6 overflow-y-auto flex flex-col gap-4">
        {/* 1. Статус подписки (бейдж #0D7A2E для active, P-022) */}
        {sub ? (
          <div
            className={[
              'rounded-xl shadow-card p-4 flex flex-col gap-1',
              sub.tone === 'active'
                ? 'bg-[#e8f5e9] border-[1.5px] border-[#0D7A2E]'
                : 'bg-card border-[1.5px] border-warning',
            ].join(' ')}
          >
            <div className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className={
                  sub.tone === 'active'
                    ? 'w-2.5 h-2.5 rounded-full bg-[#0D7A2E]'
                    : 'w-2.5 h-2.5 rounded-full bg-warning'
                }
              />
              <span
                className={[
                  'text-[13px] font-bold uppercase tracking-wider',
                  sub.tone === 'active' ? 'text-[#0D7A2E]' : 'text-warning',
                ].join(' ')}
              >
                {sub.badgeText}
              </span>
            </div>
            {sub.dateLabel && (
              <p className="text-ink text-[16px] font-medium m-0 mt-1">{sub.dateLabel}</p>
            )}
            {props.daysRemaining !== null && props.daysRemaining > 0 && (
              <p className="text-muted text-[14px] m-0">Осталось {props.daysRemaining} дн.</p>
            )}
          </div>
        ) : (
          <div className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-4">
            <p className="text-muted text-[15px] m-0" aria-live="polite">
              {props.subscriptionLoading
                ? 'Загружаем статус подписки…'
                : 'Статус подписки недоступен'}
            </p>
          </div>
        )}

        {/* 2. Тариф / цена (GET plans) — 5 состояний P-021 */}
        <div className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-4 flex flex-col gap-2">
          <p className="text-muted text-[13px] font-bold uppercase tracking-wider m-0">Ваш тариф</p>
          {props.planState === 'loading' || props.planState === 'idle' ? (
            <p className="text-muted text-[16px] m-0" aria-live="polite">
              Загружаем тариф…
            </p>
          ) : props.planState === 'error' ? (
            <p role="alert" className="text-error text-[15px] m-0">
              Не удалось загрузить тариф. Попробуйте позже.
            </p>
          ) : props.planState === 'empty' || props.plan === null ? (
            <p className="text-muted text-[16px] m-0">Тариф недоступен для вашего региона</p>
          ) : (
            <>
              <p className="text-ink text-[16px] font-medium m-0">Подписка на месяц</p>
              <div className="flex items-baseline gap-2">
                {promoApplied && props.quote ? (
                  <>
                    <span className="text-ink text-h2 font-display">
                      {formatMoney(props.quote.finalAmount, props.quote.currency)}
                    </span>
                    <span className="text-muted text-[15px] line-through">
                      {formatMoney(props.quote.baseAmount, props.quote.currency)}
                    </span>
                  </>
                ) : (
                  <span className="text-ink text-h2 font-display">
                    {formatMoney(props.plan.basePrice, props.plan.currency)}
                  </span>
                )}
                <span className="text-muted text-[14px]">/ мес</span>
              </div>
            </>
          )}
        </div>

        {/* 3. Промокод (вар.А: «Применить» → quote) — 5 состояний P-021 */}
        <div className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-4 flex flex-col gap-3">
          <TextInput
            name="promoCode"
            label="Промокод"
            value={props.promoCode}
            onChange={props.onPromoCodeChange}
            placeholder="Введите промокод"
            disabled={applying}
            maxLength={64}
          />
          <button
            type="button"
            onClick={props.onApplyPromo}
            disabled={applying || props.promoCode.trim().length === 0}
            aria-busy={applying ? true : undefined}
            className={[
              'w-full min-h-tap rounded-lg bg-transparent border-[1.5px] border-accent',
              'flex items-center justify-center gap-2 text-ink text-[16px] font-medium',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80',
              'disabled:opacity-60 disabled:cursor-not-allowed',
            ].join(' ')}
          >
            {applying ? 'Проверяем…' : 'Применить'}
          </button>

          {props.quoteState === 'error' && (
            <p role="alert" className="text-error text-sm m-0">
              {props.quoteError ?? 'Не удалось проверить промокод. Попробуйте ещё раз.'}
            </p>
          )}
          {promoApplied && props.quote?.discountPercent !== undefined && (
            <div className="flex items-center gap-2" role="status">
              <span className="w-2.5 h-2.5 rounded-full bg-[#0D7A2E]" aria-hidden="true" />
              <span className="text-[13px] font-bold uppercase tracking-wider text-[#0D7A2E]">
                Промокод применён · −{props.quote.discountPercent}%
              </span>
            </div>
          )}
          {props.quoteState === 'success' && props.quote?.promoApplied === false && (
            <p role="status" className="text-muted text-sm m-0">
              Промокод не действует — показана базовая цена.
            </p>
          )}
        </div>

        {/* 4. Кнопка «Оплатить/Продлить» — ЗАГЛУШКА (реальный checkout позже) */}
        <button
          type="button"
          onClick={props.onPay}
          aria-label={payLabel}
          className={[
            'w-full min-h-tap rounded-xl bg-cta border-b-2 border-cta-shadow',
            'text-ink text-[16px] font-bold px-4 py-3',
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
          ].join(' ')}
        >
          {payLabel}
        </button>

        {props.paymentStubOpen && (
          <div
            role="status"
            className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-4"
          >
            <p className="text-ink text-[15px] m-0">{PAYMENT_STUB_TEXT}</p>
          </div>
        )}

        {/* 5. История платежей (GET history) — 5 состояний P-021 */}
        <div className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-4 flex flex-col gap-2">
          <p className="text-muted text-[13px] font-bold uppercase tracking-wider m-0">
            История платежей
          </p>
          {props.historyState === 'loading' || props.historyState === 'idle' ? (
            <p className="text-muted text-[16px] m-0" aria-live="polite">
              Загружаем историю…
            </p>
          ) : props.historyState === 'error' ? (
            <p role="alert" className="text-error text-[15px] m-0">
              Не удалось загрузить историю платежей.
            </p>
          ) : props.historyState === 'empty' || props.history.length === 0 ? (
            <p className="text-muted text-[16px] m-0 text-center">Платежей пока нет</p>
          ) : (
            <ul className="flex flex-col gap-2 m-0 p-0 list-none">
              {props.history.map((item) => (
                <li
                  key={item.paymentId}
                  className="flex items-center justify-between gap-3 border-t border-icon-bg pt-2 first:border-t-0 first:pt-0"
                >
                  <div className="flex flex-col min-w-0">
                    <span className="text-ink text-[15px] font-medium">
                      {formatMoney(item.amount, item.currency)}
                    </span>
                    <span className="text-muted text-[13px]">{item.createdAt.slice(0, 10)}</span>
                  </div>
                  <span className="text-muted text-[13px] flex-shrink-0">
                    {HISTORY_STATUS_LABEL[item.status]}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <BottomBar variant="default" />
    </main>
  );
}
